import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    FiX, 
    FiCheck, 
    FiLayers, 
    FiCreditCard, 
    FiShield, 
    FiBriefcase, 
    FiTruck, 
    FiActivity, 
    FiHeart, 
    FiUserCheck, 
    FiUsers 
} from 'react-icons/fi';
import { esmBenefitsData } from './esmGraphData';
import HsvLogo from '../../../images/Logo/Logo HSV_01.png';
import './EsmBenefitsModal.scss';

const getNodeIcon = (nodeId) => {
    switch (nodeId) {
        case 'bank': return <FiCreditCard />;
        case 'insurance': return <FiShield />;
        case 'service': return <FiBriefcase />;
        case 'supply': return <FiTruck />;
        case 'hospital': return <FiActivity />;
        case 'clinic': return <FiHeart />;
        case 'doctor': return <FiUserCheck />;
        case 'patient': return <FiUsers />;
        default: return <FiLayers />;
    }
};

const EsmBenefitsModal = ({ isOpen, onClose, isEnglish, selectedNodeId }) => {
    const navigate = useNavigate();
    
    // Prevent body scroll when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    if (!isOpen || !selectedNodeId) return null;

    const isMedicalGroup = selectedNodeId === 'hospital' || selectedNodeId === 'clinic';
    const isFullQuadrant = selectedNodeId === 'quadrant' || selectedNodeId === 'all';
    const isMultiCardLayout = isMedicalGroup || isFullQuadrant;
    const genericData = esmBenefitsData[selectedNodeId];

    const renderQuadrant = (data, position, nodeId) => (
        <div className={`esm-quadrant esm-quadrant--${position}`}>
            <div className="esm-quadrant-header">
                <div className="esm-quadrant-icon-box">
                    {getNodeIcon(nodeId)}
                </div>
                <h3 className="esm-quadrant-title">{isEnglish ? data.titleEn : data.title}</h3>
            </div>
            <ul className="esm-quadrant-list">
                {data.items.map((item, index) => (
                    <li key={index} className="esm-quadrant-item-row">
                        <div className="esm-item-check-badge esm-item-check-badge--small">
                            <FiCheck />
                        </div>
                        <span>{item}</span>
                    </li>
                ))}
            </ul>
        </div>
    );

    const renderGenericList = (data, nodeId) => (
        <div className="esm-generic-card">
            <div className="esm-generic-card-header">
                <div className="esm-node-icon-wrapper">
                    {getNodeIcon(nodeId)}
                </div>
                <div className="esm-node-header-text">
                    <h3 className="esm-generic-title">{isEnglish ? data.titleEn : data.title}</h3>
                    <span className="esm-node-subtitle">
                        {isEnglish ? 'Value Proposition & System Benefits' : 'Giá trị gia tăng & Đóng góp vào hệ sinh thái'}
                    </span>
                </div>
            </div>

            <ul className="esm-generic-items">
                {data.items.map((item, index) => (
                    <li key={index} className="esm-generic-item-row">
                        <div className="esm-item-check-badge">
                            <FiCheck />
                        </div>
                        <span className="esm-item-text">{item}</span>
                    </li>
                ))}
            </ul>
        </div>
    );

    return (
        <div className="esm-modal-overlay" onClick={onClose}>
            <div className={`esm-modal-content ${!isMultiCardLayout ? 'esm-modal-content--generic' : ''}`} onClick={(e) => e.stopPropagation()}>
                <button className="esm-modal-close" onClick={onClose} aria-label="Close modal">
                    <FiX />
                </button>
                
                <div className="esm-modal-header">
                    <div className="esm-modal-header-top">
                        <span className="esm-header-badge">
                            <FiLayers className="esm-badge-icon" />
                            {isEnglish ? 'SMART MEDIC ECOSYSTEM' : 'HỆ SINH THÁI Y TẾ THÔNG MINH'}
                        </span>
                    </div>
                    
                    <h2 className="esm-modal-header-title">
                        {isMedicalGroup ? (
                            selectedNodeId === 'hospital' ? (
                                isEnglish ? 'Hospital, Doctor & Patient Integration Benefits' : 'Lợi Ích Liên Kết Bệnh Viện, Bác Sĩ & Bệnh Nhân'
                            ) : (
                                isEnglish ? 'Clinic, Doctor & Patient Integration Benefits' : 'Lợi Ích Liên Kết Phòng Khám, Bác Sĩ & Bệnh Nhân'
                            )
                        ) : !isMultiCardLayout && genericData ? (
                            isEnglish ? `Benefits for ${genericData.titleEn || genericData.title}` : `Lợi Ích Dành Cho ${genericData.title}`
                        ) : (
                            isEnglish ? 'Medical Chain Integration Benefits' : 'Lợi Ích Chuỗi Liên Kết Y Tế'
                        )}
                    </h2>

                    <p className="esm-modal-header-subtitle">
                        {isEnglish ? (
                            <span>Chain flow: Customers <span className="dot">•</span> Hospitals <span className="dot">•</span> Clinics <span className="dot">•</span> Doctors <span className="dot">•</span> Pharmacies</span>
                        ) : (
                            <span>Chuỗi liên kết: Khách hàng <span className="dot">•</span> Bệnh viện <span className="dot">•</span> Phòng khám <span className="dot">•</span> Bác sĩ <span className="dot">•</span> Nhà thuốc</span>
                        )}
                    </p>
                </div>

                <div className="esm-modal-body">
                    {isMedicalGroup ? (
                        /* Dedicated 3-Card Group for Hospital or Clinic with Doctor & Patient */
                        <div className="esm-quadrants-container esm-quadrants-container--trio">
                            {selectedNodeId === 'hospital' ? (
                                renderQuadrant(esmBenefitsData.hospital, 'top-left', 'hospital')
                            ) : (
                                renderQuadrant(esmBenefitsData.clinic, 'top-left', 'clinic')
                            )}
                            
                            {renderQuadrant(esmBenefitsData.doctor, 'top-right', 'doctor')}
                            
                            {/* Center Logo in the Modal */}
                            <div className="esm-modal-center-logo">
                                <img src={HsvLogo} alt="ESM Logo" className="esm-image-logo" />
                            </div>

                            {renderQuadrant(esmBenefitsData.patient, 'bottom-full', 'patient')}
                        </div>
                    ) : isFullQuadrant ? (
                        /* The 4 Quadrants layout */
                        <div className="esm-quadrants-container">
                            {renderQuadrant(esmBenefitsData.hospital, 'top-left', 'hospital')}
                            {renderQuadrant(esmBenefitsData.clinic, 'top-right', 'clinic')}
                            
                            {/* Center Logo in the Modal */}
                            <div className="esm-modal-center-logo">
                                <img src={HsvLogo} alt="ESM Logo" className="esm-image-logo" />
                            </div>

                            {renderQuadrant(esmBenefitsData.doctor, 'bottom-left', 'doctor')}
                            {renderQuadrant(esmBenefitsData.patient, 'bottom-right', 'patient')}
                        </div>
                    ) : (
                        /* Generic Layout for other individual nodes */
                        <div className="esm-generic-container">
                            {genericData && renderGenericList(genericData, selectedNodeId)}
                        </div>
                    )}
                </div>

                <div className="esm-modal-footer">
                    <button 
                        className="esm-view-details-btn" 
                        onClick={() => navigate('/tuong-tac-esm')}
                    >
                        {isEnglish ? 'View Interaction Flow Details' : 'Xem chi tiết biểu đồ tương tác'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EsmBenefitsModal;
